import { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  History as HistoryIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import {
  useGetAdminTemplateRevisionsQuery,
  type RevisionSummary,
} from '../store/hooks';

interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  templateId: string;
  currentVersion: number;
  onCompare: (versions: number[]) => void;
}

/**
 * Formats a date string to a human-readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Extracts a display name from a user ID (email or auth provider ID)
 */
function formatCreatedBy(userId: string): string {
  // If it's an email, use it directly
  if (userId.includes('@')) {
    return userId;
  }
  // If it's an auth0 ID like "google-oauth2|114527...", extract something readable
  if (userId.includes('|')) {
    const [provider] = userId.split('|');
    return provider.replace(/-/g, ' ');
  }
  // Truncate long IDs
  if (userId.length > 20) {
    return `${userId.slice(0, 17)}...`;
  }
  return userId;
}

export function VersionHistoryDrawer({
  open,
  onClose,
  templateId,
  currentVersion,
  onCompare,
}: VersionHistoryDrawerProps) {
  const [selectedVersions, setSelectedVersions] = useState<Set<number>>(
    () => new Set([currentVersion]),
  );

  const {
    data: revisionsData,
    isLoading,
    error,
  } = useGetAdminTemplateRevisionsQuery(
    { templateId },
    { skip: !open || !templateId },
  );

  const revisions = useMemo(
    () => revisionsData?.revisions ?? [],
    [revisionsData],
  );

  const handleToggleVersion = (version: number) => {
    setSelectedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(version)) {
        // Don't allow deselecting if it's the only one
        if (next.size > 1) {
          next.delete(version);
        }
      } else {
        // Max 3 versions
        if (next.size < 3) {
          next.add(version);
        }
      }
      return next;
    });
  };

  const handleCompare = () => {
    const versions = Array.from(selectedVersions).sort((a, b) => b - a);
    onCompare(versions);
    onClose();
  };

  const canCompare = selectedVersions.size >= 2;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 400, maxWidth: '100vw' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Version History</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {isLoading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load version history. Please try again.
            </Alert>
          )}

          {!isLoading && !error && revisions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <Typography color="text.secondary">
                No version history available.
              </Typography>
            </Box>
          )}

          {!isLoading && !error && revisions.length > 0 && (
            <>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  Select 2-3 versions to compare side by side
                </Typography>
              </Box>

              <List disablePadding>
                {revisions.map((revision: RevisionSummary, index: number) => {
                  const isSelected = selectedVersions.has(revision.version);
                  const isCurrent = revision.version === currentVersion;
                  const isDisabled =
                    !isSelected && selectedVersions.size >= 3;

                  return (
                    <ListItem
                      key={revision.version}
                      disablePadding
                      divider={index < revisions.length - 1}
                    >
                      <ListItemButton
                        onClick={() => handleToggleVersion(revision.version)}
                        disabled={isDisabled}
                        sx={{
                          py: 1.5,
                          bgcolor: isSelected ? 'primary.50' : 'transparent',
                          '&:hover': {
                            bgcolor: isSelected ? 'primary.100' : 'grey.100',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            disabled={isDisabled}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body1"
                                fontWeight={isCurrent ? 600 : 400}
                              >
                                Version {revision.version}
                              </Typography>
                              {isCurrent && (
                                <Chip
                                  label="current"
                                  size="small"
                                  color="primary"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="span"
                              >
                                {formatDate(revision.created_at)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="span"
                                sx={{ mx: 1 }}
                              >
                                •
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                component="span"
                              >
                                {formatCreatedBy(revision.created_by)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.25 }}
                              >
                                {revision.section_count} section
                                {revision.section_count !== 1 ? 's' : ''} •{' '}
                                {revision.title}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedVersions.size} selected (max 3)
          </Typography>
          <Button
            variant="contained"
            startIcon={<CompareIcon />}
            onClick={handleCompare}
            disabled={!canCompare}
          >
            Compare Versions
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default VersionHistoryDrawer;
